import { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
  onClose: () => void;
}

export default function Toast({ message, type = 'success', duration = 3000, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  if (!isVisible) return null;

  const bgColor = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    info: 'bg-blue-50 border-blue-200',
  }[type];

  const textColor = {
    success: 'text-green-700',
    error: 'text-red-700',
    info: 'text-blue-700',
  }[type];

  const Icon = {
    success: CheckCircle,
    error: AlertCircle,
    info: AlertCircle,
  }[type];

  return (
    <div className={`fixed bottom-4 right-4 z-50 p-4 rounded-lg border ${bgColor} flex items-start gap-3 max-w-sm shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-300`}>
      <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${textColor}`} />
      <p className={`text-sm font-medium ${textColor} flex-1`}>{message}</p>
      <button
        onClick={() => {
          setIsVisible(false);
          onClose();
        }}
        className={`flex-shrink-0 p-1 hover:bg-white/40 rounded transition-colors ${textColor}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
